import { useEffect, useState } from "react";
import Header from "@/components/Header";
import ShopCard from "@/components/ShopCard";
import { api } from "@/lib/api";
import { useSearchParams, Link } from "react-router-dom";
import { Store, Search } from "lucide-react";

export default function Marketplace() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const search = params.get("search") || "";
  const fulfillment = params.get("fulfillment") || "";
  const [query, setQuery] = useState(search);

  useEffect(() => {
    setLoading(true);
    api.get("/marketplace/shops", { params: { search: search || undefined, fulfillment: fulfillment || undefined } })
      .then((r) => setShops(r.data))
      .finally(() => setLoading(false));
  }, [search, fulfillment]);

  const applySearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (query) next.set("search", query); else next.delete("search");
    setParams(next);
  };
  const setFilter = (val) => {
    const next = new URLSearchParams(params);
    if (val) next.set("fulfillment", val); else next.delete("fulfillment");
    setParams(next);
  };

  return (
    <>
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#857163]">Store Fronts</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24]">
              Meet the <span className="text-[#FF7E67]">storefronts</span>
            </h1>
            <p className="mt-2 text-[#857163]">Browse indie cake pop shops — with or without a map pin. Tap a shop to see their menu, place a custom request, or order directly from the vendor.</p>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            <Link to="/classifieds" className="cp-btn-ghost inline-flex items-center gap-1.5 text-sm" data-testid="classifieds-cta">
              <Store className="w-4 h-4" /> Browse Classifieds
            </Link>
            <Link to="/seller/apply" className="cp-btn-primary inline-flex items-center gap-1.5" data-testid="become-seller-cta">
              <Store className="w-4 h-4" /> Become a seller
            </Link>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center mb-6">
          <form onSubmit={applySearch} className="flex-1 max-w-md relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#857163]" />
            <input
              className="cp-input !pl-11"
              placeholder="Search storefronts by name, city, or tagline…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="marketplace-search-input"
            />
          </form>
          <div className="flex gap-2">
            {[
              { key: "", label: "All storefronts" },
              { key: "pickup", label: "Local pickup" },
              { key: "shipping", label: "Ships" },
            ].map((f) => (
              <button
                key={f.key || "all"}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                  fulfillment === f.key
                    ? "bg-[#3B2C24] text-white border-[#3B2C24]"
                    : "bg-white border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
                }`}
                data-testid={`marketplace-filter-${f.key || "all"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#857163]"><div className="cp-cakepop-loader" /> Loading storefronts…</div>
        ) : shops.length === 0 ? (
          <div className="cp-card p-10 text-center">
            <div className="font-heading text-2xl text-[#3B2C24] mb-2">The store front is quiet</div>
            <p className="text-[#857163] mb-4">No storefronts match your search yet.</p>
            <Link to="/seller/apply" className="cp-btn-primary inline-flex items-center gap-1.5">
              <Store className="w-4 h-4" /> Be the first seller
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="marketplace-grid">
            {shops.map((s) => <ShopCard key={s.user_id} shop={s} />)}
          </div>
        )}
      </main>
    </>
  );
}
